import {PreviewPosition} from 'components/customer/Preview3D/types';
import type {DoorFrontsMode} from 'components/customer/RoomPlanner/helpers/doorFronts';

export type PlannerViewMode = '3D' | 'PLAN' | 'ELEVATION';

export interface PlannerViewState {
    mode: PlannerViewMode;
    preset: PreviewPosition | null;
}

export interface ViewOption {
    id: PreviewPosition;
    label: string;
}

export const PERSPECTIVE_OPTIONS: ViewOption[] = [
    {id: 'LEFT', label: 'Left'},
    {id: 'RIGHT', label: 'Right'},
];

export const ELEVATION_OPTIONS: ViewOption[] = [
    {id: 'LEFT_LEVELED', label: 'Left'},
    {id: 'RIGHT_LEVELED', label: 'Right'},
    {id: 'FRONT', label: 'Front'},
];

export const PLAN_PRESET: PreviewPosition = 'TOP_WITHOUT_EDITING';
// Top Right. Reset view and the 3D button both return here.
export const DEFAULT_PERSPECTIVE_PRESET: PreviewPosition = 'RIGHT';
export const DEFAULT_ELEVATION_PRESET: PreviewPosition = 'LEFT_LEVELED';

/** Direction change smaller than this is treated as pan or zoom, not an orbit. */
export const ORBIT_THRESHOLD_RADIANS = (1.5 * Math.PI) / 180;

export const viewStateForPreset = (
    position: PreviewPosition | null
): PlannerViewState | null => {
    if (position === 'LEFT' || position === 'RIGHT') {
        return {mode: '3D', preset: position};
    }

    if (position === PLAN_PRESET) {
        return {mode: 'PLAN', preset: null};
    }

    if (
        position === 'LEFT_LEVELED' ||
        position === 'RIGHT_LEVELED' ||
        position === 'FRONT'
    ) {
        return {mode: 'ELEVATION', preset: position};
    }

    return null;
};

export const optionsForMode = (mode: PlannerViewMode): ViewOption[] => {
    if (mode === '3D') return PERSPECTIVE_OPTIONS;
    if (mode === 'ELEVATION') return ELEVATION_OPTIONS;
    return [];
};

export const iconForViewOption = (id: string): string => {
    if (id === 'LEFT') return 'preview/Isometric-Left.svg';
    if (id === 'RIGHT') return 'preview/Isometric-Right.svg';
    if (id === 'LEFT_LEVELED') return 'preview/Perspective-Left.svg';
    if (id === 'RIGHT_LEVELED') return 'preview/Perspective-Right.svg';
    if (id === 'FRONT') return 'preview/Perspective-Front.svg';
    return 'preview/Solid.svg';
};

/**
 * Left, Right, and elevation chips only. Edit-room cameras stay out of the switcher.
 * @param {string} value - data-preset from a view option button
 * @return {boolean} True when the value is a highlighted view option
 */
export const isViewOptionId = (value: string): value is PreviewPosition => {
    return (
        value === 'LEFT' ||
        value === 'RIGHT' ||
        value === 'LEFT_LEVELED' ||
        value === 'RIGHT_LEVELED' ||
        value === 'FRONT'
    );
};

export const directionChanged = (
    dot: number,
    thresholdRadians = ORBIT_THRESHOLD_RADIANS
): boolean => {
    const clamped = Math.min(1, Math.max(-1, dot));
    return Math.acos(clamped) > thresholdRadians;
};

/**
 * Orbiting leaves the current preset. Plan and Elevation return to 3D.
 * Pan and zoom never call this, because their direction does not change.
 * @param {PlannerViewMode} mode - View selected before the orbit.
 * @param {PreviewPosition | null} preset - Highlighted option, if any.
 * @return {PlannerViewState | null} Next view state, or null when nothing changes.
 */
export const viewStateAfterDirectionChange = (
    mode: PlannerViewMode,
    preset: PreviewPosition | null
): PlannerViewState | null => {
    if (mode === '3D' && preset == null) return null;
    return {mode: '3D', preset: null};
};

export type TopBarControl =
    | '3D'
    | 'Plan'
    | 'Elevation'
    | 'Reset view'
    | 'wireframe'
    | 'Fronts'
    | 'Dimensions'
    | 'Print';

export interface TopBarPresentation {
    left: TopBarControl[];
    right: TopBarControl[];
    resetViewVisible: boolean;
    doorFrontsMode: DoorFrontsMode;
    dimensionsHighlighted: boolean;
    wireframeLabel: 'Rendered' | 'Wireframe';
    options: ViewOption[];
}

export const getTopBarPresentation = ({
    mode,
    doorFrontsMode,
    showArrows,
    isWireframeMode,
    showPrint,
}: {
    mode: PlannerViewMode;
    doorFrontsMode: DoorFrontsMode;
    showArrows: boolean;
    isWireframeMode: boolean;
    showPrint: boolean;
}): TopBarPresentation => {
    const left: TopBarControl[] = ['3D', 'Plan', 'Elevation'];
    if (mode === '3D') left.push('Reset view');

    const right: TopBarControl[] = ['wireframe', 'Fronts', 'Dimensions'];
    if (showPrint) right.push('Print');

    return {
        left,
        right,
        resetViewVisible: mode === '3D',
        doorFrontsMode,
        dimensionsHighlighted: showArrows,
        wireframeLabel: isWireframeMode ? 'Wireframe' : 'Rendered',
        options: optionsForMode(mode),
    };
};
